import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import Select, { components } from 'react-select';
import { useSelector } from 'react-redux';
import AdvancedSearchUtility from '../../../../utility/AdvancedSearchUtility';
import SearchType from '../../../../utility/types/SearchType';
import ListFilterModal from '../../../common/modals/ListFilterModal/ListFilterModal';
import CustomMultiValueLabel
  from '../../../common/RaaLists/AdvancedListSearch/CustomMultiValueLabel/CustomMultiValueLabel';
import { connectModalContainerContextToComponent } from '../../../../contexts/ModalContext/ModalContext';
import FacetType from '../../../../utility/types/facetType';
import { selectSensorsData } from '../../../../redux/slices/alertFormSlice/alertFormSlice';

const CustomMultiValueRemove = (props) => {
  // eslint-disable-next-line react/prop-types
  const { data, selectedChannels } = props;

  // Hide remove button only if:
  // - option is fixed
  // - AND there are 0 selected channels
  // eslint-disable-next-line react/prop-types
  if (data.isFixed && selectedChannels.length > 0) {
    return null;
  }

  return <components.MultiValueRemove {...props} />;
};

function ChannelSearchFilters({
  filters,
  updateFilters,
  removeFilter,
  getFilterTooltip,
  getFilterByFacetTypeId,
  onApply,
  setModalJSX,
}) {
  const { selectedChannels } = useSelector(selectSensorsData);
  const selectRef = useRef(null);

  const options = AdvancedSearchUtility.getFilterSelectOptions(
    SearchType.CHANNEL,
    // disable channel type filter if not already applied
    filters.find((f) => f.facetId === FacetType.CHANNEL_TYPE) ? [FacetType.CHANNEL_TYPE] : [],
  );
  // remove device id and channel type filter options since we moved to separate dropdown
  const filteredOptions = options
    .filter((opt) => opt.value !== FacetType.CHANNEL_TYPE && opt.value !== FacetType.DEVICE_ID);

  const selectedOptions = AdvancedSearchUtility.getSelectedOptionsByValue(
    filteredOptions,
    filters,
  );

  const openFilterModal = (facetId) => {
    if (selectRef && selectRef.current) {
      selectRef.current.blur();
    }

    const filterToEdit = getFilterByFacetTypeId(facetId);

    setModalJSX(
      <ListFilterModal
        facetTypeId={facetId}
        searchType={SearchType.CHANNEL}
        facetSelectOptions={[]}
        filterToEdit={filterToEdit}
        updateFilters={updateFilters}
        getSearchedResults={onApply}
        handleSetSelectedSearchFilters={() => {}}
      />,
    );
  };

  const handleDropdownChange = (values, action) => {
    if (action.action === 'select-option') {
      const selected = values[values.length - 1];
      openFilterModal(selected.value);
    }

    if (action.action === 'remove-value') {
      removeFilter(action.removedValue.value);
      const updated = filters.filter(
        (f) => f.facetTypeId !== action.removedValue.value,
      );
      onApply(updated);
    }
  };

  const handleChipClick = (e, value) => {
    e.preventDefault();
    e.stopPropagation();
    openFilterModal(value);
  };

  return (
    <form>
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
      <label className="sr-only" htmlFor="channelFilters">Filters</label>

      <Select
        ref={selectRef}
        inputId="channelFilters"
        name="channelFilters"
        isMulti
        isClearable={false}
        options={filteredOptions}
        value={selectedOptions}
        onChange={handleDropdownChange}
        placeholder="Add a filter"
        styles={{ container: (base) => ({ ...base, width: '100%' }) }}
        components={{
          MultiValueLabel: (props) => {
            // eslint-disable-next-line react/prop-types
            const { data } = props;

            // eslint-disable-next-line react/prop-types
            const isEditable = !(data.isFixed && selectedChannels.length > 0);

            return (
              <CustomMultiValueLabel
                {...props}
                handleMultiValueClick={isEditable ? handleChipClick : () => {}}
                getFilterTooltip={getFilterTooltip}
                searchType={SearchType.CHANNEL}
              />
            );
          },
          MultiValueRemove: (props) => (
            <CustomMultiValueRemove
              {...props}
              selectedChannels={selectedChannels}
            />
          ),
        }}
      />
    </form>
  );
}

ChannelSearchFilters.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  filters: PropTypes.array.isRequired,
  updateFilters: PropTypes.func.isRequired,
  removeFilter: PropTypes.func.isRequired,
  getFilterTooltip: PropTypes.func.isRequired,
  getFilterByFacetTypeId: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  setModalJSX: PropTypes.func.isRequired,
};

export { ChannelSearchFilters };
export default connectModalContainerContextToComponent(ChannelSearchFilters);
